import React, { useEffect, useState, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import style from "./ContentTable.module.css";
import Icon from "@/ui/icon/Icon"; // Adjust the import path as needed
import { SoundWaveIco, CurrentModuleIcon } from "@/ui/icon/CustomIcons"; // Adjust the import path as needed
import { Link, useParams } from "react-router-dom";
import { useGetCourseUnits } from "@/features/courses/hooks/useGetCourseUnits";
import { secondsToMinutesSeconds } from "@/utils/helpers";
import MarqueeText from "@/ui/marqueeText/MarqueeText";
import { useUser } from "@/features/auth/hooks/useUser";

import Button from "@/ui/button/Button";
import { useUnwatchModule } from "@/features/courses/hooks/useUnwatchModule";
import SubscribeToPremium from "@/features/payments/components/subscribeToPremium/SubscribeToPremium";
import ModuleProgress from "@/features/courses/components/moduleProgress/ModuleProgress";
import { useLocalized } from "@/i18n/useLocalized";

/**
 * `embedded` is the mobile "Sadržaj" tab, where CourseWatching already renders
 * the progress card — or the premium upsell — directly above the tab strip.
 * Without this the same card was drawn twice, one under the other.
 */
function ContentTable({ embedded = false }) {
	const { t } = useTranslation();
	const { courseSlug, moduleSlug, unitSlug } = useParams();
	const sidebarRef = useRef(null);
	const { course, modules, isPending, isSuccess, isError, error } =
		useGetCourseUnits({ courseSlug });

	const { user, isLoggedIn, isPending: userIsPending } = useUser();

	const { unwatchModule } = useUnwatchModule({ courseSlug });
	const l = useLocalized();

	const [openModules, setOpenModules] = useState([]);
	const [modalModule, setModalModule] = useState(null);

	const isUnitLocked = (unit) => {
		if (unit.is_preview) return false;
		if (!user) return true;
		if (!course.is_free && !user.is_subscribed) return true;

		return false;
	};

	// open every module that is not completed
	useEffect(() => {
		if (openModules.length > 0) return;
		if (!modules) return;
		setOpenModules(
			modules
				.filter(
					(module) =>
						module.completion < 100 ||
						module.units.find((unit) => unit.slug == unitSlug)
				)
				.map((module) => module.slug)
		);
	}, [modules]);

	function toggleModule(slug) {
		if (!openModules.includes(slug)) {
			setOpenModules([...openModules, slug]);
		} else {
			setOpenModules(openModules.filter((slug2) => slug2 != slug));
		}
	}

	const handleModuleCheck = (module, event) => {
		event.stopPropagation();
		setModalModule(module);
		sidebarRef.current?.scrollTo({ top: 0, behavior: "smooth" });
	};

	const handleConfirmUncheck = async () => {
		if (modalModule?.slug)
			await unwatchModule({ moduleSlug: modalModule.slug });
		setModalModule(null);
	};

	if (isError)
		return <>{t("player:contentTable.error", { error: JSON.stringify(error) })}</>;

	if (isPending || userIsPending || !isSuccess) return <Skelton />;

	const coursePercentComplete = Math.floor(
		(100 * course.completed_units) / course.total_units
	);

	return (
		<aside className={style.sidebar} ref={sidebarRef}>
			<div className={style.header}>
				<Link to="/" className={style.back}>
					<Icon icon="fa-circle-xmark" />
					<span>{t("player:contentTable.back")}</span>
				</Link>

				{modalModule && user?.is_subscribed ? (
					<div className={style.moduleWarning}>
						<div className={style.warningContent}>
							<div className={style.header}>
								<div className={style.icoHolder}>
									<Icon
										icon="fa-triangle-exclamation"
										className={style.warningIcon}
									/>
								</div>
								<h2>{t("player:contentTable.resetTitle")}</h2>
							</div>

							<p>
								<Trans
									i18nKey="player:contentTable.resetConfirm"
									values={{ name: l(modalModule, "name") }}
									components={{ b: <b /> }}
								/>
							</p>
							<div className={style.warningActions}>
								<Button
									onClick={() => setModalModule(null)}
									className={style.cancelBtn}
								>
									<Icon icon="fa-times" />
									{t("player:contentTable.cancel")}
								</Button>
								<Button
									onClick={handleConfirmUncheck}
									className={style.confirmBtn}
								>
									<Icon icon="fa-trash" />
									{t("player:contentTable.confirm")}
								</Button>
							</div>
						</div>
					</div>
				) : embedded ? null : (
					<>
						{(user?.is_subscribed || course?.is_free) &&
						isLoggedIn ? (
							<div className={style.moduleProgressHolder}>
								<ModuleProgress
									user={user}
									course={course}
									coursePercentComplete={
										coursePercentComplete
									}
								/>
							</div>
						) : (
							<SubscribeToPremium
								register={!isLoggedIn && course?.is_free}
							/>
						)}
					</>
				)}
			</div>
			<div className={style.body}>
				<div className={style.modules}>
					{modules.map((module) => (
						<div
							key={module.slug}
							className={`${style.module} ${
								module.completion >= 100 ? style.completed : ""
							}`}
						>
							<div className={style.info}>
								<div
									className={style.icoHolder}
									onClick={(e) =>
										module.completion >= 100 &&
										handleModuleCheck(module, e)
									}
								>
									{module.completion >= 100 ? (
										<Icon
											icon="fa-square-check"
											iconType="fa-solid"
											className={style.clickable}
										/>
									) : module.slug == moduleSlug ? (
										<CurrentModuleIcon
											completion={module.completion}
										/>
									) : (
										<Icon icon="fa-layer-group" />
									)}
								</div>
								<div
									className={style.title}
									onClick={() => toggleModule(module.slug)}
								>
									{l(module, "name")}
								</div>
								<div
									className={`${style.expend} ${
										openModules.includes(module.slug)
											? style.active
											: ""
									}`}
									onClick={() => toggleModule(module.slug)}
								>
									<Icon icon="fa-chevron-down" />
								</div>
							</div>
							{openModules.includes(module.slug) && (
								<div className={style.units}>
									{module.units.map((unit) => (
										<Link
											to={`/courses/${courseSlug}/${module.slug}/${unit.slug}/`}
											key={unit.slug}
											className={`
												${style.unit} 
												${unit?.user_activity?.is_completed ? style.completed : ""}
												${isUnitLocked(unit) ? style.locked : unit.slug == unitSlug ? style.active : ""}
											`}
										>
											<div className={style.icoHolder}>
												{isUnitLocked(unit) ? (
													<span
														className={
															style.lockedIco
														}
													>
														<Icon icon="fa-lock" />
													</span>
												) : unit.slug == unitSlug ? (
													unit.question_count ? (
														<Icon icon="fa-question" />
													) : (
														<SoundWaveIco />
													)
												) : (
													<Icon
														icon={
															unit.unit_type ===
															"video"
																? "fa-film"
																: "fa-seal-question"
														}
													/>
												)}
											</div>

											<MarqueeText
												className={style.title}
												text={l(unit, "name")}
												maxChars={35}
											/>
											<div className={style.duration}>
												{unit?.is_liked && (
													<span
														className={
															style.likedIco
														}
													>
														<Icon icon="fa-heart" />
													</span>
												)}
												{unit?.user_activity
													?.is_completed && (
													<span
														className={
															style.completedIco
														}
													>
														<Icon icon="fa-check" />
													</span>
												)}
												<span>
													{unit.unit_type === "video"
														? secondsToMinutesSeconds(
																unit.video_duration
															)
														: t("player:contentTable.questions", {
																count: unit.question_count ?? 0,
															})}
												</span>
											</div>
										</Link>
									))}
								</div>
							)}
						</div>
					))}
				</div>
			</div>
		</aside>
	);
}

function Skelton() {
	return <aside className={style.sidebar}></aside>;
}

export default ContentTable;
